/**
 * BulkLinkDispositifsModal — V36.19
 *
 * Modale "Lier en masse" : sélectionner des dispositifs et/ou estimateurs
 * à appliquer sur N nomenclatures en une seule action.
 *
 * Modes :
 *   - replace : la liste fournie remplace l'existante
 *   - add     : ajoute (sans doublons)
 *   - remove  : retire ces IDs
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Layers, Search, Building, Calculator } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODES = [
  { value: 'replace', label: 'Remplacer', help: 'Écrase la liste existante par votre sélection.' },
  { value: 'add', label: 'Ajouter', help: 'Ajoute sans toucher aux liaisons existantes.' },
  { value: 'remove', label: 'Retirer', help: 'Retire vos sélections des liaisons existantes.' },
];

const ItemList = ({ items, selected, setSelected, color = 'indigo', label, icon: Icon, testidPrefix }) => {
  const [q, setQ] = useState('');
  const selectedSet = new Set(selected);
  const visible = (items || [])
    .filter((d) => {
      const ql = q.trim().toLowerCase();
      if (!ql) return true;
      return ((d.nom || d.name || '') + ' ' + (d.code || '') + ' ' + (d.description || ''))
        .toLowerCase().includes(ql);
    })
    .slice()
    .sort((a, b) => {
      const aTest = /test/i.test(a.nom || a.name || '');
      const bTest = /test/i.test(b.nom || b.name || '');
      if (aTest !== bTest) return aTest ? 1 : -1;
      const aSel = selectedSet.has(a.id);
      const bSel = selectedSet.has(b.id);
      if (aSel !== bSel) return aSel ? -1 : 1;
      return (a.nom || a.name || '').localeCompare(b.nom || b.name || '', 'fr');
    });
  const allSel = visible.length > 0 && visible.every((d) => selectedSet.has(d.id));

  return (
    <div className={`space-y-2 p-3 border rounded-lg bg-gradient-to-br from-slate-50/60 to-${color}-50/30`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className={`text-sm font-semibold flex items-center gap-2 text-${color}-900`}>
          {Icon && <Icon className="h-4 w-4" />}
          {label}
          <span className={`ml-1 inline-flex items-center justify-center text-[11px] font-bold bg-${color}-100 text-${color}-700 rounded-full px-2 py-0.5`}>
            {selectedSet.size}/{items.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className={`text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded-md w-40 focus:outline-none focus:ring-1 focus:ring-${color}-300`}
              data-testid={`${testidPrefix}-search`}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (allSel) {
                const ids = new Set(visible.map((d) => d.id));
                setSelected(selected.filter((id) => !ids.has(id)));
              } else {
                const next = new Set(selected);
                visible.forEach((d) => next.add(d.id));
                setSelected([...next]);
              }
            }}
            className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-${color}-200 bg-white text-${color}-700 hover:bg-${color}-50 flex-shrink-0`}
            data-testid={`${testidPrefix}-toggle-all`}
          >
            {allSel ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-2 text-center">Aucun élément</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
          {visible.map((d) => {
            const checked = selectedSet.has(d.id);
            const isTest = /test/i.test(d.nom || d.name || '');
            const toggle = () => {
              if (checked) setSelected(selected.filter((id) => id !== d.id));
              else setSelected([...selected, d.id]);
            };
            return (
              <button
                type="button"
                key={d.id}
                onClick={toggle}
                className={`text-left flex items-start gap-2 px-2.5 py-1.5 rounded-md border transition-all ${
                  checked
                    ? `bg-${color}-50 border-${color}-300 ring-1 ring-${color}-200`
                    : `bg-white border-slate-200 hover:border-${color}-200 hover:bg-slate-50`
                } ${isTest ? 'opacity-60' : ''}`}
                data-testid={`${testidPrefix}-item-${d.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={toggle}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className={`text-xs font-medium leading-snug break-words ${checked ? `text-${color}-900` : 'text-slate-800'}`}>
                    {d.nom || d.name}
                    {isTest && <span className="ml-1 text-[9px] font-bold text-amber-700 bg-amber-100 px-1 py-0.5 rounded">TEST</span>}
                  </div>
                  {d.code && <div className="text-[10px] text-slate-500 font-mono">{d.code}</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function BulkLinkDispositifsModal({
  open, onOpenChange, selectedNomenclatures = [], dispositifs = [], estimateurs = [],
  onApplied,
}) {
  const [mode, setMode] = useState('add');
  const [selectedDispositifs, setSelectedDispositifs] = useState([]);
  const [selectedEstimateurs, setSelectedEstimateurs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [applyDispositifs, setApplyDispositifs] = useState(true);
  const [applyEstimateurs, setApplyEstimateurs] = useState(false);

  useEffect(() => {
    if (open) {
      setMode('add');
      setSelectedDispositifs([]);
      setSelectedEstimateurs([]);
      setApplyDispositifs(true);
      setApplyEstimateurs(false);
    }
  }, [open]);

  const handleApply = async () => {
    if (!applyDispositifs && !applyEstimateurs) {
      toast.error('Cochez au moins un type à appliquer');
      return;
    }
    const payload = {
      nomenclature_ids: selectedNomenclatures.map((n) => n.id),
      mode,
    };
    if (applyDispositifs) payload.dispositifs = selectedDispositifs;
    if (applyEstimateurs) payload.estimateurs = selectedEstimateurs;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/nomenclatures/bulk-link`,
        payload,
        { headers: getAuthHeader() },
      );
      const d = res.data;
      toast.success(
        `${d.updated} nomenclature${d.updated > 1 ? 's' : ''} mise${d.updated > 1 ? 's' : ''} à jour`
        + (d.skipped ? ` (${d.skipped} non modifiée${d.skipped > 1 ? 's' : ''})` : ''),
      );
      onOpenChange?.(false);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la liaison en masse');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="bulk-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-900">
            <Layers className="h-5 w-5" />
            Lier en masse — {selectedNomenclatures.length} nomenclature{selectedNomenclatures.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Appliquez en une seule action les dispositifs et/ou estimateurs sur toutes les nomenclatures sélectionnées.
          </DialogDescription>
        </DialogHeader>

        {/* Preview liste */}
        <div className="bg-slate-50 rounded-lg p-3 max-h-24 overflow-y-auto">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1">
            Nomenclatures ciblées
          </div>
          <div className="text-xs text-slate-700 leading-relaxed">
            {selectedNomenclatures.slice(0, 8).map((n) => n.name || n.nom).join(' · ')}
            {selectedNomenclatures.length > 8 && <span className="text-slate-500"> + {selectedNomenclatures.length - 8} autre(s)</span>}
          </div>
        </div>

        {/* Mode */}
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">Mode d'application</div>
          <div className="grid grid-cols-3 gap-2">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all text-center ${
                  mode === m.value
                    ? 'bg-indigo-600 text-white border-indigo-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'
                }`}
                data-testid={`bulk-mode-${m.value}`}
              >
                <div>{m.label}</div>
                <div className={`text-[10px] mt-0.5 font-normal leading-tight ${
                  mode === m.value ? 'text-white/85' : 'text-slate-500'
                }`}>{m.help}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Section Dispositifs */}
        <div className="space-y-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={applyDispositifs}
              onCheckedChange={(v) => setApplyDispositifs(!!v)}
              data-testid="bulk-apply-dispositifs"
            />
            <span className="text-sm font-semibold text-indigo-900">
              Appliquer aux dispositifs
            </span>
          </label>
          {applyDispositifs && (
            <ItemList
              items={dispositifs}
              selected={selectedDispositifs}
              setSelected={setSelectedDispositifs}
              color="indigo"
              label="Dispositifs"
              icon={Building}
              testidPrefix="bulk-dispositif"
            />
          )}
        </div>

        {/* Section Estimateurs */}
        <div className="space-y-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={applyEstimateurs}
              onCheckedChange={(v) => setApplyEstimateurs(!!v)}
              data-testid="bulk-apply-estimateurs"
            />
            <span className="text-sm font-semibold text-emerald-900">
              Appliquer aux estimateurs
            </span>
          </label>
          {applyEstimateurs && (
            <ItemList
              items={estimateurs}
              selected={selectedEstimateurs}
              setSelected={setSelectedEstimateurs}
              color="emerald"
              label="Estimateurs"
              icon={Calculator}
              testidPrefix="bulk-estimateur"
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleApply}
            disabled={submitting}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
            data-testid="bulk-apply-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Appliquer aux {selectedNomenclatures.length} nomenclature{selectedNomenclatures.length > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
